import React, { useEffect, useState } from 'react';
import { DatePicker, message } from 'antd';
import moment, { Moment } from 'moment';
import { renderIntlLabel } from '@/utils';

const { RangePicker } = DatePicker;

type RangeValue = [Moment | null, Moment | null] | null;

interface RangeDateDynamicLimitProps {
  isShowTime?: boolean;
  defaultRangeDate?: RangeValue; // 初始起止日期范围
  rangeCount?: number; // 时间跨度天数
  value?: RangeValue;
  onChange?: (..._) => void;
  onBlur?: (..._) => void;
}

const RangeDateDynamicLimit: React.FC<RangeDateDynamicLimitProps> = ({
  isShowTime = false,
  defaultRangeDate,
  rangeCount = 180,
  value,
  onChange,
  onBlur,
}) => {
  // 动态选择的临时日期范围
  const [dates, setDates] = useState<RangeValue>(null);
  const [selectedValue, setSelectedValue] = useState<RangeValue>(null);

  // 动态限制范围
  const disabledDate = (current: Moment) => {
    // 未选择时
    if (!dates || (!dates[0] && !dates[1])) {
      if (!defaultRangeDate) {
        return false;
      }
      let startDisable = false;
      let endDisable = false;
      if (defaultRangeDate[0]) {
        startDisable = current.isBefore(defaultRangeDate[0]);
      }
      if (defaultRangeDate[1]) {
        endDisable = current.isAfter(defaultRangeDate[1]);
      }
      return startDisable || endDisable;
    }
    // 选择日期时
    // *** 判断rangeCount是否超过defaultRangeDate默认区间
    let tempLateCount = rangeCount - 1;
    let tempEarlyCount = rangeCount - 1;
    const tempStartDate = moment(dates[0]).clone().hour(0).minute(0).second(0).millisecond(0);
    const tempEndDate = moment(dates[1]).clone().hour(23).minute(59).second(59).millisecond(999);
    if (
      tempStartDate &&
      tempStartDate.clone().add(rangeCount, 'days').isAfter(defaultRangeDate?.[1])
    ) {
      tempLateCount = defaultRangeDate?.[1].diff(tempStartDate, 'days');
    }
    if (
      tempEndDate &&
      tempEndDate.clone().add(-rangeCount, 'days').isBefore(defaultRangeDate?.[0])
    ) {
      tempEarlyCount = tempEndDate.diff(defaultRangeDate?.[0], 'days');
    }
    const tempRangeCount = Math.min(tempLateCount, tempEarlyCount);
    const tooLate = tempStartDate && current.diff(tempStartDate, 'days') > tempRangeCount;
    const tooEarly = tempEndDate && tempEndDate.diff(current, 'days') > tempRangeCount;
    return !!tooEarly || !!tooLate;
  };

  const formatTime2StartEnd = (list: RangeValue) => {
    const result: any = new Array(2);
    if (Array.isArray(list) && list.length === 2 && list[0] && list[1]) {
      if (isShowTime) {
        result[0] = moment(list[0]);
        result[1] = moment(list[1]);
      } else {
        result[0] = moment(list[0]).hour(0).minute(0).second(0).millisecond(0);
        result[1] = moment(list[1]).hour(23).minute(59).second(59).millisecond(999);
      }
    }
    return result;
  };

  const isOverRangeCount = (attr) => {
    if (attr && attr[0] && attr[1]) {
      return attr[1].diff(attr[0], 'days') > rangeCount - 1;
    }
    return false;
  };

  const handleRangeDateChange = (val) => {
    const tempAttr = val;
    // 校验所选日期是否超过限定日期跨度
    if (isOverRangeCount(tempAttr)) {
      message.warning(
        renderIntlLabel('guest_msg_selected_date_exceeds_the_maxRange', { rangeCount }),
      );
      tempAttr[0] = tempAttr[1].clone().add(-rangeCount, 'days');
    }
    // 只有日期时格式化起止时间
    const formatValues = formatTime2StartEnd(tempAttr);
    setSelectedValue(formatValues);
    if (typeof onChange === 'function') {
      onChange(formatValues);
    }
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  useEffect(() => {
    const formatValues = formatTime2StartEnd(value);
    setSelectedValue(formatValues);
  }, [value]);

  return (
    <>
      <RangePicker
        value={dates || selectedValue}
        disabledDate={disabledDate}
        showTime={
          isShowTime
            ? {
                hideDisabledOptions: true,
              }
            : false
        }
        onCalendarChange={(val) => setDates(val)}
        onChange={handleRangeDateChange}
        onOpenChange={onOpenChange}
        onBlur={onBlur}
      />
    </>
  );
};

export default RangeDateDynamicLimit;
